import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { Briefcase, Trash2, Plus, Loader2, CheckCircle2 } from 'lucide-react';

const AdminJobs = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    type: 'IT',
    category: '',
    desc: '',
    skills: ''
  });

  const fetchJobs = async () => {
    setFetching(true);
    try {
      const snap = await getDocs(collection(db, 'jobs'));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setJobs(list);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await addDoc(collection(db, 'jobs'), {
        ...formData,
        createdAt: serverTimestamp(),
        createdAtString: new Date().toLocaleString()
      });
      
      setMessage('Job successfully saved to Firebase Database!');
      setFormData({ title: '', type: 'IT', category: '', desc: '', skills: '' });
      await fetchJobs();
    } catch (error) {
      console.error('Error posting job to Firebase:', error);
      alert('Error posting job to Firebase database: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job listing?')) return;
    try {
      await deleteDoc(doc(db, 'jobs', id));
      setJobs(prev => prev.filter(j => j.id !== id));
    } catch (err) {
      alert('Failed to delete job: ' + err.message);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900 text-white p-6 rounded-3xl shadow-md">
        <div>
          <h2 className="text-2xl font-black">Manage Job Openings</h2>
          <p className="text-xs text-slate-400 mt-1">Live jobs stored securely in Firebase database (No Sheets).</p>
        </div>
        <div className="text-xs bg-slate-800 px-4 py-2 rounded-xl text-slate-300 font-semibold border border-slate-700">
          Total Active Listings: <span className="font-bold text-amber-400">{jobs.length}</span>
        </div>
      </div>

      {message && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl flex items-center gap-2 font-medium">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" /> {message}
        </div>
      )}

      {/* Post New Job Form */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-600" /> Post New Job Opening
          </h3>
          <span className="text-xs font-bold px-3 py-1 bg-blue-100 text-blue-700 rounded-full uppercase">Firebase DB</span>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Job Title */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Job Title *</label>
              <input
                required
                type="text"
                placeholder="e.g. Senior React Developer or Pipe Welder"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>

            {/* Job Type (IT or Non-IT) */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Role Type *</label>
              <select
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                <option value="IT">IT Role (Shown on IT Careers)</option>
                <option value="Non-IT">Non-IT Role (Shown on Non-IT Careers)</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Industry / Category *</label>
              <input
                required
                type="text"
                placeholder="e.g. Development, Cloud, Construction, Healthcare"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              />
            </div>

            {/* Key Skills */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Key Skills (comma separated)</label>
              <input
                type="text"
                placeholder="e.g. React, Node.js, SQL OR SMAW, GTAW, 6G"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition"
                value={formData.skills}
                onChange={(e) => setFormData({...formData, skills: e.target.value})}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Job Description *</label>
            <textarea
              required
              rows={4}
              placeholder="Provide a detailed job description, duties, expectations, and qualifications..."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition"
              value={formData.desc}
              onChange={(e) => setFormData({...formData, desc: e.target.value})}
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full md:w-auto px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg transition flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="animate-spin h-5 w-5" /> Saving to Firebase...</> : 'Publish Job Listing'}
          </button>
        </form>
      </div>

      {/* Existing Jobs List */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-black text-slate-900">Current Live Jobs ({jobs.length})</h3>
          <button
            onClick={fetchJobs}
            className="text-xs font-bold text-blue-600 hover:underline"
          >
            Refresh List
          </button>
        </div>

        {fetching ? (
          <div className="py-12 flex justify-center items-center gap-2 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin text-amber-500" /> Loading jobs from database...
          </div>
        ) : jobs.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <Briefcase className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p className="font-semibold text-slate-600">No jobs currently in Firebase database.</p>
            <p className="text-xs mt-1">Fill out the form above to publish your first live opening.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map((job) => (
              <div key={job.id} className="p-5 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-slate-300 transition shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                      job.type === 'IT' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {job.type}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-400">{job.category}</span>
                  </div>
                  <h4 className="font-bold text-slate-900 text-base mb-1">{job.title}</h4>
                  <p className="text-xs text-slate-600 line-clamp-2 mb-2">{job.desc}</p>
                  <p className="text-[11px] text-slate-500 italic"><strong>Skills:</strong> {job.skills}</p>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200/60 flex justify-end">
                  <button
                    onClick={() => handleDeleteJob(job.id)}
                    className="flex items-center gap-1 text-xs text-red-500 font-bold hover:text-red-700 transition"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminJobs;